// janus-engine/ability/charged-cast.ts — Wizard v9 WP5 (owner 2026-10-09): the CHARGED-ITEM CAST in the browser (wands, staffs, rings,
// SCROLLS). A charged cast is a spell / ability granted by an inventory row's enchantment (its `charged_spell` / `charged_ability`
// entries) and cast with use_ability_variable_1 = the mode and use_ability_variable_2 = that inventory row's id. There is no dedicated
// scroll path: a scroll (item_type 'scroll') is the generic charged-item cast — its template's item_charges = the spell's level, so one
// cast drains it to 0. OWNER RULING 2026-10-09 (consume scrolls on cast): a scroll costs at least 1 charge and is removed at 0 (PHP never
// removed a spent scroll and let a cantrip scroll cost 0 — settleSourceCharges).
//
// The rules, each the twin of the legacy PHP it retires (board R-207):
//   · cost (consume_inventory_charges, consume_inventory_charges.php:45, called from janus_post_roll_change_ability_resources.php:95):
//     charged_spell pays the spell's spell_level (no upcast); charged_ability pays ability_activation_charges (none authored → free).
//     Paid once per cast whatever the targets do, clamped to [0, item_charges]; a `change_charges` row on the item. The commit persists
//     the source row's charge through the ability truth commit's `charged_source` frame (authority = the row's possess chain).
//   · no mana (template_spell_roll.php:347, use_ability_pre_conditions.php's skip list) — template-spell-roll.ts reads ctx.itemCast.
//   · uncounterable (counterspell_inc.php:495) — counterspell.ts UNCOUNTERABLE_VARIABLES already carried it.
//   · no components (check_spell_components.php:126) — spell-components.ts COMPONENT_FREE_MODES already carried it.
//   · the stat source (spell_is_cast_from_magical_item.php:28): a spell attack / a contest with no authored contester skill rolls the
//     MAGIC skill (Mind) instead of Bending Weapon Skill — castSkillEntries() below, used by attack-roll.ts and contesting-roll.ts.
//   · the cast-time gate (charges left, the grant) is ability-preconditions.ts (L486–525 / L209–274) — unchanged.
// Wizard v9 scroll_spell_damage_multiplier (Evocation "Runes of Overload") rides the same fact: scrollSpellDamageMultiplier().
import { type JanusCtx, type Row, handBack, insertRoll, itemField, record, requiredInt, scalar, upper } from './context.js';
import { CIRCLE_EVALUATED, casterLayerProviders, ctxProviderReaders } from './caster-spell-layers.js';
import { spellInCircleScope } from './spell-circle-scope.js';
import { chargeReductionDelta } from '../resources/reduction-guard.js';
import { logPreventedReduction, resourceReductionReaders } from './resource-reduction.js';

export const CHARGED_CAST_MODES = ['charged_spell', 'charged_ability'] as const;
export type ChargedCastMode = typeof CHARGED_CAST_MODES[number];
/** Skill::getId('magic') — the item-sourced casting skill (essence Mind). */
export const MAGIC_SKILL = 'ABE36CBD-1142-9809-FA05-C7DC4263B43C';
export const SCROLL_SPELL_DAMAGE_MULTIPLIER = 'scroll_spell_damage_multiplier';
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** The cast-from-item fact a charged cast carries on its JanusCtx (`ctx.chargedSource` for the use, `ctx.itemCast` while ITS dispatch runs). */
export interface ItemCast {
  mode: ChargedCastMode;
  /** the source inventory row's id (use_ability_variable_2) */
  inventoryId: string;
  /** the source inventory row as the page holds it (current_item_charges / item_charges read through it) */
  source: Row;
  /** the source row's item_type ('scroll', 'wand', 'staff' …) */
  itemType: string;
}

export const isChargedCastMode = (value: unknown): value is ChargedCastMode =>
  typeof value === 'string' && (CHARGED_CAST_MODES as readonly string[]).includes(value);

/** The readers resolving the source row (AbilityUseReaders satisfies it). */
export interface ChargedSourceReaders {
  resolveInventory?: (id: string) => Row | null;
  inventoryField?: (row: Row | null, key: string) => unknown;
  /** the catalog base item of a thinned instance (the page's cached row may carry only its inventory bucket) */
  definition?: (id: string) => Row | null;
}

/** The source row's item_type: its own items column, else its catalog base item's (inventory_item_id). null = not held on the page. */
export function sourceItemType(read: ChargedSourceReaders, source: Row): string | null {
  const own = String(itemField(source, 'item_type') ?? '').toLowerCase();
  if (own) return own;
  const base = upper(read.inventoryField?.(source, 'inventory_item_id') ?? record(source.inventory)?.inventory_item_id);
  const def = UUID.test(base) ? read.definition?.(base) ?? null : null;
  const viaBase = String(itemField(def, 'item_type') ?? '').toLowerCase();
  return viaBase || null;
}

/**
 * use_ability_inc.php's discern_item_id(variable_2) for a charged cast: the source inventory row. null = not a charged cast;
 * `{ handback }` = a charged cast the page cannot resolve (named, before anything is drawn).
 */
export function resolveChargedSource(read: ChargedSourceReaders, variable1: unknown, variable2: unknown): ItemCast | { handback: string } | null {
  if (!isChargedCastMode(variable1)) return null;
  const id = upper(variable2);
  if (!UUID.test(id)) return { handback: 'charged_cast:source-id-invalid' };
  if (!read.resolveInventory || !read.inventoryField) return { handback: 'charged_cast:readers' };
  const source = read.resolveInventory(id);
  if (!source) return { handback: 'charged_cast:source-row-not-held' };
  if (upper(read.inventoryField(source, 'inventory_id')) !== id) return { handback: 'charged_cast:source-row-mismatch' };
  // the item type decides a scroll's consumption (owner 2026-10-09): unknown → fetch the base item and retry (the page's definition-not-held lane)
  const itemType = sourceItemType(read, source);
  if (itemType === null) {
    const base = upper(read.inventoryField(source, 'inventory_item_id') ?? record(source.inventory)?.inventory_item_id);
    return { handback: UUID.test(base) ? `preflight:charged_cast:source-definition-not-held:${base}` : 'charged_cast:source-item-type-not-held' };
  }
  return { mode: variable1, inventoryId: id, source, itemType };
}

/** The ItemCast THIS dispatch runs as: the use's charged source, only while the dispatch is the charged cast itself (a nested janus() —
 *  a trigger, a payload — carries no variables and is not cast from the item). */
export function activeItemCast(ctx: Pick<JanusCtx, 'chargedSource'>, input: { useAbilityVariable1: unknown; useAbilityVariable2: unknown }): ItemCast | null {
  const source = ctx.chargedSource;
  if (!source || input.useAbilityVariable1 !== source.mode || upper(input.useAbilityVariable2) !== source.inventoryId) return null;
  return source;
}

/** spell_is_cast_from_magical_item(): the contester entries a spell attack / contest rolls with — the authored list, else Magic for an
 *  item-sourced cast (the caller's default — Bending — otherwise). */
export function castSkillEntries(ctx: Pick<JanusCtx, 'itemCast' | 'spellGrantFromInventory'>, authored: unknown[]): unknown[] {
  return authored.length || !(ctx.itemCast || ctx.spellGrantFromInventory) ? authored : [MAGIC_SKILL];
}

const lenientInt = (value: unknown): number => {
  const v = scalar(value);
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\s*-?\d+/.test(v) ? parseInt(v, 10) : 0;
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

/**
 * consume_inventory_charges() for the active charged cast: the source row's charges drop by the cast's cost (spell_level / the ability's
 * ability_activation_charges), clamped to [0, item_charges], once per cast; logged as `change_charges` on the item. Runs inside
 * janusPostRollChangeAbilityResources (PHP's order: after the ability's own pools and the nested-inventory unit, before the mana delta).
 */
export function billChargedCastSource(ctx: JanusCtx, ability: Row, caster: Row, useAbilityVariable1: unknown): void {
  const cast = ctx.itemCast;
  if (!cast || useAbilityVariable1 !== cast.mode) return;
  if (ctx.chargedConsume || ctx.chargesChanged.some((c) => upper(c.holder) === cast.inventoryId && upper(c.item_id) === cast.inventoryId)) return; // once per cast
  const raw = ctx.read.value(cast.source, 'current_item_charges');
  if (raw == null || raw === '') return; // empty($inventory_arr current_item_charges) && !== 0 → PHP bills nothing
  if (cast.mode === 'charged_ability') {
    const activation = scalar(ctx.read.value(ability, 'ability_activation_charges'));
    if (activation == null || activation === '' || activation === 0 || activation === '0') return;
  }
  // empty($caster_arr['inventory']) bills nothing in PHP — but the commit binds the source row to THIS caster's possess chain, so a caster
  // that really holds the source always has inventory: a page row showing none is an unheld read, never a free cast.
  if (!ctx.read.entries(caster, 'inventory').length && !(Array.isArray(caster.inventory) && caster.inventory.length))
    handBack('charged_cast:caster-inventory-not-held');
  const cost = cast.mode === 'charged_ability'
    ? lenientInt(ctx.read.value(ability, 'ability_activation_charges') ?? 1)
    : lenientInt(ctx.read.value(ability, 'spell_level') ?? 1);
  settleSourceCharges(ctx, caster, cast.inventoryId, cast.source, cast.itemType, cost, raw);
}

/**
 * The source row's charge after a charged cast paid `cost` (clamped to [0, item_charges], a `change_charges` row) — shared by the direct cast
 * and a timed charged cast's finish (template-spell-roll.ts). OWNER RULING 2026-10-09 — CONSUME SCROLLS ON CAST: a SCROLL costs at least 1
 * charge (a cantrip scroll is used up too) and, at 0, ONE unit of it is removed (ctx.chargedConsume → the commit's `charged_source.consume`
 * → delete_inventory qty 1, which logs the consumption); a stack with more units keeps a fresh next unit (its charge back to item_charges).
 * Wands, staffs and every other charged item keep recharging at 0.
 */
export function settleSourceCharges(ctx: JanusCtx, caster: Row, inventoryId: string, source: Row, itemType: string, cost: number, raw: unknown): void {
  const scroll = itemType === 'scroll';
  if (scroll) cost = Math.max(1, cost);
  if (cost <= 0) return;
  if (chargeReductionDelta(caster, -cost, resourceReductionReaders(ctx), source) === 0) {
    logPreventedReduction(ctx, caster, 'charges');
    if (scroll) ctx.chargedConsume = { inventoryId };
    return;
  }
  const current = requiredInt(raw, 'charged_cast:source-charges-invalid');
  const max = Math.max(0, lenientInt(ctx.read.value(source, 'item_charges') ?? 0));
  const next = Math.max(0, Math.min(Math.max(0, current - cost), max));
  if (scroll && next === 0) {
    ctx.chargedConsume = { inventoryId };
    const qty = lenientInt(ctx.read.inventoryField?.(source, 'item_qty') ?? 1);
    if (qty > 1 && max !== current) ctx.chargesChanged.push({ holder: inventoryId, item_id: inventoryId, before: current, charges: max });
    return;
  }
  ctx.chargesChanged.push({ holder: inventoryId, item_id: inventoryId, before: current, charges: next });
  insertRoll(ctx, { roll_type_1: 'change_charges', roll_item_id: inventoryId, by: caster, to: caster, roll_result: next });
}

/** The spell in hand was cast from a SCROLL: the active charged cast's source, or — a TIMED charged cast's FINISH (timed-finish.ts; no
 *  ctx.itemCast there: PHP's finish keeps Bending and its own billing) — the casting status's stored source row. */
function castFromScroll(ctx: JanusCtx, ability: Row): boolean {
  if (ctx.itemCast) return ctx.itemCast.itemType === 'scroll';
  const finish = ctx.timedFinish;
  return !!finish?.variable2Arr && finish.abilityId === upper(itemField(ability, 'item_id'))
    && sourceItemType(ctx.read, finish.variable2Arr) === 'scroll';
}

/**
 * scroll_spell_damage_multiplier (Wizard v9, Evocation L25 "Runes of Overload" — "Evocation spells you cast from scrolls deal double
 * damage"; int, circle-scoped): the factor every damage fragment of a SPELL cast from an inventory row whose item_type is `scroll` is
 * multiplied by — the HIGHEST value among the caster's in-scope providers (never multiplied together). 1 = none. Hands back (pre-draw:
 * the dry runs reach it) when a provider cannot be proved.
 */
export function scrollSpellDamageMultiplier(ctx: JanusCtx, caster: Row, ability: Row): { factor: number; source: string } {
  const none = { factor: 1, source: '' };
  if (!castFromScroll(ctx, ability) || String(itemField(ability, 'item_type') ?? '') !== 'spell') return none;
  const read = ctxProviderReaders(ctx);
  // CIRCLE_EVALUATED: the scope predicate judges if_circle_of_magic_cast itself, so an in-scope STATUS provider is provable (WP1 review)
  const providers = casterLayerProviders(read, caster, SCROLL_SPELL_DAMAGE_MULTIPLIER, (row) => spellInCircleScope(read, row, ability), CIRCLE_EVALUATED);
  if (providers === undefined) handBack('scroll_spell_damage_multiplier:provider-unproved');
  let best = none;
  for (const provider of providers) {
    const n = lenientInt(read.value(provider, SCROLL_SPELL_DAMAGE_MULTIPLIER));
    if (n > best.factor) best = { factor: n, source: String(itemField(provider, 'item_name') ?? '') || 'Scroll Spell Damage' };
  }
  return best;
}
